// ══════════════════════════════════════════════════════════════
// COMPASS – Error Reporting Service
// ══════════════════════════════════════════════════════════════
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { ApiConfiguration } from '../api/api-configuration';
import { reportError } from '../api/fn/client-error-reporting/report-error';
import { ClientErrorReportDto } from '../api/models/client-error-report-dto';

/**
 * Builds structured, sanitized error reports from unhandled errors
 * and POSTs them to `POST /client-errors` via the generated API client.
 */
@Injectable({ providedIn: 'root' })
export class ErrorReportingService {
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject(ApiConfiguration);

  reportError(error: unknown): void {
    try {
      const message = this.extractMessage(error);
      const stackTrace = this.extractStack(error);

      const report: ClientErrorReportDto = {
        message: this.sanitize(message),
        stackTrace: stackTrace ? this.sanitize(stackTrace) : undefined,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        metadata: { environment: environment.environment } as ClientErrorReportDto['metadata'],
      };

      reportError(this.http, this.apiConfig.rootUrl, { body: report }).subscribe();
    } catch {
      // Reporting must never throw — silently swallow
    }
  }

  private extractMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return String(error);
  }

  private extractStack(error: unknown): string | null {
    if (error instanceof Error && error.stack) {
      return error.stack;
    }
    return null;
  }

  /**
   * Strip sensitive tokens, JWTs, and email addresses from text
   * so they never appear in error reports.
   */
  private sanitize(text: string): string {
    return text
      .replace(/Bearer\s+[A-Za-z0-9\-._~+/]+=*/g, 'Bearer [REDACTED]')
      .replace(/[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{20,}/g, '[JWT REDACTED]')
      .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[EMAIL REDACTED]');
  }
}
